import { useEffect, useState } from "react";
import Joi from "joi";
import { connect, useDispatch } from "react-redux";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  getColleges,
  getPageBlocks,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import { updateStudentResource } from "../../../../../action/masters/studentResourceActionCreator";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import {
  baseURL,
  cloneDeep,
  booleanOptions,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  IEditStudentResource,
  IStudentResourceData,
} from "../../../../../models/master/studentResource";
import { setStudentResource } from "../../../../../store/slices/master/studentResourceSlice";
import { ConfigUserTypes } from "../../../../../models/dashboard/adminDashboard";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";

const EditStudentResource = (props: IEditStudentResource) => {
  const dispatch = useDispatch();
  const {
    updateStudentResource,
    studentResource,
    colleges,
    getColleges,
    getPageBlocks,
    getUserPageBlocks,
    pageBlocks,
    setShowEdit,
    getAllStudentResources,
  } = props;

  // states

  const [data, setData] = useState<IStudentResourceData>(
    cloneDeep(studentResource || {})
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const ssoOptionsList = Object.entries(booleanOptions).map(([key, value]) => ({
    id: value,
    name: key,
  }));
  const [isSSOEnabled, setIsSSOEnabled] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    setData({ ...data, [event.target.name]: event.target.value });
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const fileType = selectedFile.type;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (studentResource) {
      let _studentResource = cloneDeep(studentResource);
      setData(_studentResource);
      if (_studentResource.icon) {
        const imageUrl = baseURL + _studentResource.icon;
        setImagePreview(imageUrl);
      }
      if (_studentResource.sso) {
        setIsSSOEnabled(true);
      }
    }
  }, [studentResource]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllStudentResources();
      setShowEdit(false);
    }
  };

  const urlRegex = /^https?:\/\/([a-z0-9]{2,}(-[a-z0-9]{2,})*\.)+[a-z0-9]{2,6}(\/[a-z0-9#?=&%\/\-_\.]*)?$/i;
  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    description: Joi.string().required().label("Description is required"),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select College"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
    sso: Joi.string().when("isSSOEnabled", {
      is: true,
      then: Joi.string().required().regex(urlRegex).label("Enter Valid SSO"),
      otherwise: Joi.string().allow("", null).optional(),
    }),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
    isSSOEnabled: isSSOEnabled,
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("name", data.name);
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("url", data.url);
      isSSOEnabled
        ? formData.append("sso", data.sso)
        : formData.append("sso", "");
      formData.append("isActive", "true");
      data.pageBlockIds.map((item: number, index: number) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.collegeIds.map((item: number, index: number) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      setLoading(true);
      setIsDisabled(true);
      try {
        await updateStudentResource(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    getColleges();
    getUserPageBlocks(ConfigUserTypes.Student);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setStudentResource(undefined));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update Student Resource
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Name "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.name}
                  name={"name"}
                  maxLength={50}
                />
                {errors?.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="d-flex align-items-end gap-2">
                  <div className="w-100 inputfile-wrap">
                    <label className="profile_details_text">
                      Image<i className="label-asterisk"> * </i>
                    </label>
                    <input
                      type="file"
                      name="file"
                      className="form-control"
                      required
                      onChange={onFileChange}
                      value={errors?.file && ''}
                    />
                  </div>
                  {imagePreview && (
                    <div className="">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ maxWidth: "120px", maxHeight: "50px" }}
                      />
                    </div>
                  )}
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">
                  Description<i className="label-asterisk"> * </i>
                </label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>

              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"URL "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.url}
                  name={"url"}
                  maxLength={500}
                />
                {errors?.url && (
                  <div className="text-danger">{errors.url}</div>
                )}
              </div>

              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Is SSO Enabled "}
                  onChange={(e: any) => setIsSSOEnabled(e.target.value)}
                  isMandatory={true}
                  value={isSSOEnabled}
                  name={"isSSOEnabled"}
                  optionsList={ssoOptionsList}
                />
              </div>

              {isSSOEnabled && (
                <div className="col-md-6 form-group">
                  <FloatingLabelTextInput
                    label={"SSO"}
                    onChange={onChange}
                    isMandatory={true}
                    value={data.sso}
                    name={"sso"}
                    maxLength={500}
                  />
                  {errors?.sso && (
                    <div className="text-danger">{errors.sso}</div>
                  )}
                </div>
              )}

              <div className="col-md-6 form-group">
                <MultipleSelect
                  label={"Page-Block "}
                  onChange={onChange}
                  isMandatory={true}
                  values={data.pageBlockIds as number[]}
                  name={"pageBlockIds"}
                  optionList={sortAlphabetically(pageBlocks)}
                />
                {errors?.pageBlockIds && (
                  <div className="text-danger">{errors.pageBlockIds}</div>
                )}
              </div>
              <div
                className={
                  isSSOEnabled === true
                    ? "col-md-12 form-group"
                    : "col-md-6 form-group"
                }
              >
                <MultipleSelect
                  label={"College "}
                  onChange={onChange}
                  isMandatory={true}
                  values={data.collegeIds as number[]}
                  name={"collegeIds"}
                  optionList={sortAlphabetically(colleges)}
                />
                {errors?.collegeIds && (
                  <div className="text-danger">{errors.collegeIds}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateStudentResource: (data: any, id: number, callBack: Function) =>
    updateStudentResource(data, id, callBack),

  getColleges: () => getColleges(),
  getPageBlocks: () => getPageBlocks(),
  getUserPageBlocks: (userType: number) => getUserPageBlocks(userType),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditStudentResource);
